import { useMemo } from "react";

import { TableRow } from "@prequel/shared-components";
import { Badge, BadgeColor } from "@prequel/shared-components";

import { ApiRoutes } from "@/api/client";
import { useAllModels, useModels } from "@/api/queries/models";
import ResourceTable, { type FilterField } from "@/components/ResourceTable";
import Timestamp from "@/components/Timestamp";
import { ExistingModel } from "@/types";

const MODEL_TABLE_COLUMNS = ["Model Name", "Description", "Created At"];

// map ExistingModel to TableRow
const rowMapper = (model: ExistingModel): TableRow => ({
  id: model.id,
  to: `${ApiRoutes.MODELS}/${model.id}`,
  cells: [
    {
      value: <Badge color={BadgeColor.GRAY} text={model.model_name} />,
    },
    {
      value: model.description || <span className="grow text-gray-300 italic">undefined</span>,
    },
    { value: <Timestamp timestamp={model.created_at} /> },
  ],
});

const ModelsTable = () => {
  const rowMapperMemo = useMemo(() => rowMapper, []);

  // The API matches model_name exactly, so the options come from the full set rather than
  // from whichever page happens to be on screen.
  const { data: allModels, isLoading: modelsLoading } = useAllModels();
  const filterFields: FilterField[] = useMemo(
    () => [
      {
        key: "model_name",
        label: "Model Name",
        queryParam: "model_name",
        loading: modelsLoading,
        options: (allModels ?? []).map((model) => ({ value: model.model_name, label: model.model_name })),
      },
    ],
    [allModels, modelsLoading],
  );

  return (
    <ResourceTable<ExistingModel>
      columns={MODEL_TABLE_COLUMNS}
      useQuery={useModels}
      rowMapper={rowMapperMemo}
      emptyMessage="No models found. Models will show up here."
      filterFields={filterFields}
    />
  );
};

export default ModelsTable;
